import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Search, Eye, AlertTriangle,
  RefreshCw, ChevronLeft, ChevronRight, CheckCircle, Clock, Archive, Flag
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';

const RecordsManagement = () => {
  const [records, setRecords] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [manualReviewRecords, setManualReviewRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [disputeTotal, setDisputeTotal] = useState(0);
  const [manualReviewTotal, setManualReviewTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    status: '',
    industry: '',
    plan_tier: '',
    search: ''
  });
  const [selectedRecord, setSelectedRecord] = useState(null);

  const limit = 50;

  useEffect(() => {
    if (activeTab === 'disputes') {
      fetchDisputes();
    } else if (activeTab === 'manual-review') {
      fetchManualReviewRecords();
    } else {
      fetchRecords();
    }
  }, [page, filters, activeTab]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getRecords({
        skip: page * limit,
        limit,
        ...filters
      });
      setRecords(response.data.records || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch records:', error);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDisputedRecords({
        skip: page * limit,
        limit
      });
      setDisputes(response.data.disputes || []);
      setDisputeTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
      toast.error('Failed to load disputed records');
    } finally {
      setLoading(false);
    }
  };

  const fetchManualReviewRecords = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getRecordsUnderReview();
      setManualReviewRecords(response.data.records || []);
      setManualReviewTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch manual review records:', error);
      toast.error('Failed to load records under review');
    } finally {
      setLoading(false);
    }
  };

  const handleClearReview = async (recordId) => {
    try {
      await adminAPI.clearRecordReview(recordId);
      toast.success('Record cleared - included in tier calculation');
      fetchManualReviewRecords();
    } catch (error) {
      console.error('Failed to clear review:', error);
      toast.error('Failed to clear record review');
    }
  };

  const handleMarkNoAction = async (recordId) => {
    try {
      await adminAPI.markRecordReviewedNoAction(recordId);
      toast.success('Record marked as reviewed - excluded from tier calculation');
      fetchManualReviewRecords();
    } catch (error) {
      console.error('Failed to mark no action:', error);
      toast.error('Failed to mark record');
    }
  };

  const fetchRecordDetail = async (recordId) => {
    try {
      const response = await adminAPI.getRecordDetail(recordId);
      setSelectedRecord(response.data);
    } catch (error) {
      console.error('Failed to fetch record detail:', error);
      toast.error('Failed to load record details');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-500/20 text-green-400 border-0"><CheckCircle className="w-3 h-3 mr-1" /> Co-signed</Badge>;
      case 'pending_acknowledgment':
        return <Badge className="bg-amber-500/20 text-amber-400 border-0"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'draft':
        return <Badge className="bg-gray-500/20 text-gray-400 border-0"><FileText className="w-3 h-3 mr-1" /> Draft</Badge>;
      case 'archived':
        return <Badge className="bg-blue-500/20 text-blue-400 border-0"><Archive className="w-3 h-3 mr-1" /> Archived</Badge>;
      case 'disputed':
        return <Badge className="bg-red-500/20 text-red-400 border-0"><Flag className="w-3 h-3 mr-1" /> Disputed</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-0">{status}</Badge>;
    }
  };

  const getPlanBadge = (tier) => {
    const colors = {
      free: 'bg-gray-500/20 text-gray-400',
      trial: 'bg-blue-500/20 text-blue-400',
      starter: 'bg-purple-500/20 text-purple-400',
      growth: 'bg-green-500/20 text-green-400'
    };
    return <Badge className={`${colors[tier] || colors.free} border-0`}>{tier}</Badge>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString();
  };

  const statuses = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_acknowledgment', label: 'Pending Acknowledgment' },
    { value: 'published', label: 'Co-signed' },
    { value: 'archived', label: 'Archived' },
    { value: 'disputed', label: 'Disputed' }
  ];

  const planTiers = [
    { value: '', label: 'All Plans' },
    { value: 'free', label: 'Free' },
    { value: 'trial', label: 'Trial' },
    { value: 'starter', label: 'Starter' },
    { value: 'growth', label: 'Growth' }
  ];

  const refreshActions = (
    <Button
      onClick={() => activeTab === 'disputes' ? fetchDisputes() : fetchRecords()}
      variant="outline"
      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
    >
      <RefreshCw className="w-4 h-4 mr-2" />
      Refresh
    </Button>
  );

  return (
    <AdminLayout title="Records Management" breadcrumbs={[{ label: 'Records' }]} actions={refreshActions}>
      <div className="space-y-6" data-testid="records-management">

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5]">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
            All Records
          </TabsTrigger>
          <TabsTrigger value="disputes" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
            Disputed Records
          </TabsTrigger>
          <TabsTrigger value="manual-review" className="data-[state=active]:bg-[#F0B429] data-[state=active]:text-[#0A0F1E] text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
            Manual Review {manualReviewTotal > 0 && <span className="ml-1 px-1.5 py-0.5 bg-amber-600 text-white rounded-full text-xs">{manualReviewTotal}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4 mt-4">
          {/* Filters */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                  <Input
                    placeholder="Search by job title or company..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10 bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                  />
                </div>
              </div>

              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-4 py-2 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5] text-[#0A0F1E] focus:border-[#1B5AFF] outline-none"
              >
                {statuses.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              <select
                value={filters.plan_tier}
                onChange={(e) => setFilters({ ...filters, plan_tier: e.target.value })}
                className="px-4 py-2 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5] text-[#0A0F1E] focus:border-[#1B5AFF] outline-none"
              >
                {planTiers.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </Card>

          {/* Records Table */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
              </div>
            ) : records.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FFFFFF] border-b border-[#E8EDF5]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Record</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Company</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Plan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {records.map((record, index) => (
                        <tr key={record.id || index} className="hover:bg-[#FFFFFF]/50 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-[#0A0F1E] text-sm font-medium block">{record.job_title}</span>
                              <span className="text-[#5B6472] text-xs">{record.id?.slice(0, 8)}...</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#0A0F1E] text-sm">{record.employee_name || 'Unknown'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#0A0F1E] text-sm">{record.company_name}</span>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(record.status)}
                          </td>
                          <td className="px-4 py-3">
                            {getPlanBadge(record.plan_tier)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#5B6472] text-sm">{formatDate(record.created_at)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              onClick={() => fetchRecordDetail(record.id)}
                              variant="ghost"
                              size="sm"
                              className="text-[#1B5AFF] hover:text-[#0A0F1E] hover:bg-[#1B5AFF]/20"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8EDF5]">
                  <span className="text-[#5B6472] text-sm">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      variant="outline"
                      size="sm"
                      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setPage(p => p + 1)}
                      disabled={(page + 1) * limit >= total}
                      variant="outline"
                      size="sm"
                      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-[#5B6472]">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No records found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="disputes" className="space-y-4 mt-4">
          {/* Disputed Records */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
              </div>
            ) : disputes.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FFFFFF] border-b border-[#E8EDF5]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Record</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Concern</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Resolution</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {disputes.map((dispute, index) => (
                        <tr key={dispute.id || index} className="hover:bg-[#FFFFFF]/50 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-[#0A0F1E] text-sm font-medium block">{dispute.record_title}</span>
                              <span className="text-[#5B6472] text-xs">{dispute.company_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#0A0F1E] text-sm">{dispute.employee_name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#5B6472] text-sm max-w-xs truncate block">{dispute.concern}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`border-0 ${
                              dispute.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                              dispute.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {dispute.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#5B6472] text-sm truncate max-w-xs block">
                              {dispute.resolution || 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#5B6472] text-sm">{formatDate(dispute.created_at)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8EDF5]">
                  <span className="text-[#5B6472] text-sm">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, disputeTotal)} of {disputeTotal}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      variant="outline"
                      size="sm"
                      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setPage(p => p + 1)}
                      disabled={(page + 1) * limit >= disputeTotal}
                      variant="outline"
                      size="sm"
                      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-[#5B6472]">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No disputed records</p>
                <p className="text-sm">All records are in good standing</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Manual Review Tab */}
        <TabsContent value="manual-review" className="space-y-4 mt-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-[#E8EDF5]">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h3 className="text-[#0A0F1E] font-semibold">Records Requiring Manual Review</h3>
              </div>
              <p className="text-[#5B6472] text-sm mt-1">
                These records have been flagged due to: rating below 3.0, negative rehire eligibility, and substantive negative closing assessment (100+ words).
                Admin must review before including in tier calculation.
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-6 h-6 text-[#1B5AFF] animate-spin" />
              </div>
            ) : manualReviewRecords.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="manual-review-table">
                  <thead className="bg-[#FFFFFF]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Job Title / Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Rating</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Rehire</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Flagged Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8EDF5]">
                    {manualReviewRecords.map((record, index) => (
                      <tr key={record.id || index} className="hover:bg-[#FFFFFF]/50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-[#0A0F1E] text-sm font-medium">{record.employee_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-[#0A0F1E] text-sm font-medium block">{record.job_title}</span>
                            <span className="text-[#5B6472] text-xs">{record.employer_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className="bg-red-500/20 text-red-400 border-0">
                            {record.rating?.toFixed(1) || 'N/A'}/5
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`border-0 ${
                            record.rehire_eligible === true || record.rehire_eligible === 'yes' 
                              ? 'bg-green-500/20 text-green-400'
                              : record.rehire_eligible === 'conditional'
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-red-500/20 text-red-400'
                          }`}>
                            {record.rehire_eligible === true || record.rehire_eligible === 'yes' 
                              ? 'Yes' 
                              : record.rehire_eligible === 'conditional' 
                                ? 'Conditional' 
                                : 'No'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[#5B6472] text-sm">{formatDate(record.manual_review_flagged_at)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => fetchRecordDetail(record.id)}
                              variant="ghost"
                              size="sm"
                              className="text-[#5B6472] hover:text-[#0A0F1E] h-8 w-8 p-0"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleClearReview(record.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-[#FFFFFF]"
                              title="Clear - Include in Tier"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Clear
                            </Button>
                            <Button
                              onClick={() => handleMarkNoAction(record.id)}
                              size="sm"
                              variant="outline"
                              className="border-[#E8EDF5] text-[#5B6472] hover:bg-[#F5F8FF] hover:text-[#0A0F1E]"
                              title="Mark Reviewed - Exclude from Tier"
                            >
                              Exclude
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16 text-[#5B6472]">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400 opacity-50" />
                <p className="text-lg">No records pending review</p>
                <p className="text-sm">All flagged records have been reviewed</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Detail Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#0A0F1E]">Record Details</h2>
                <Button
                  onClick={() => setSelectedRecord(null)}
                  variant="ghost"
                  className="text-[#5B6472] hover:text-[#0A0F1E]"
                >
                  Close
                </Button>
              </div>

              <div className="space-y-6">
                {/* Record Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <h3 className="text-[#5B6472] text-xs uppercase mb-2">Job Title</h3>
                    <p className="text-[#0A0F1E] font-medium">{selectedRecord.record?.job_title}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <h3 className="text-[#5B6472] text-xs uppercase mb-2">Company</h3>
                    <p className="text-[#0A0F1E] font-medium">{selectedRecord.employer?.company_name}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <h3 className="text-[#5B6472] text-xs uppercase mb-2">Employee</h3>
                    <p className="text-[#0A0F1E] font-medium">
                      {selectedRecord.employee?.first_name} {selectedRecord.employee?.last_name}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <h3 className="text-[#5B6472] text-xs uppercase mb-2">Status</h3>
                    {getStatusBadge(selectedRecord.record?.status)}
                  </div>
                </div>

                {/* Audit Trail */}
                <div>
                  <h3 className="text-[#0A0F1E] font-semibold mb-3">Audit Trail</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedRecord.audit_trail?.length > 0 ? (
                      selectedRecord.audit_trail.map((entry, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                          <div className="w-2 h-2 rounded-full bg-[#1B5AFF] mt-2" />
                          <div>
                            <p className="text-[#0A0F1E] text-sm">{entry.action}</p>
                            <p className="text-[#5B6472] text-xs">{formatDate(entry.timestamp)}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-[#5B6472] text-sm">No audit trail available</p>
                    )}
                  </div>
                </div>

                {/* Disputes */}
                {selectedRecord.disputes?.length > 0 && (
                  <div>
                    <h3 className="text-[#0A0F1E] font-semibold mb-3">Disputes</h3>
                    <div className="space-y-2">
                      {selectedRecord.disputes.map((dispute, index) => (
                        <div key={index} className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                          <p className="text-red-400 font-medium">{dispute.concern}</p>
                          <p className="text-[#5B6472] text-sm mt-1">
                            Status: {dispute.status} | Resolution: {dispute.resolution || 'Pending'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default RecordsManagement;
